import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchPublicReviews, EMPTY_STATS, MIN_REVIEWS_TO_SHOW } from "@/lib/reviews";
import { devWarn } from "@/lib/logger";

const INITIAL = { loaded: false, reviews: [], count: 0, minToShow: MIN_REVIEWS_TO_SHOW, stats: EMPTY_STATS };
const ReviewsContext = createContext(INITIAL);

/**
 * SEO audit batch 3, Part 1. Loads the Review collection + ReviewStats once per
 * page load (in the browser only — the prerender has no data, so every review
 * block is absent from the static HTML exactly as it is on a site with no
 * reviews yet). Every consumer renders nothing until `loaded` is true, so the
 * hydration never drifts.
 */
export const ReviewsProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL);

  useEffect(() => {
    let cancelled = false;
    fetchPublicReviews()
      .then((data) => { if (!cancelled) setState({ loaded: true, ...data }); })
      .catch((err) => {
        devWarn("Reviews: could not load /api/reviews:", err?.message || err);
        if (!cancelled) setState({ ...INITIAL, loaded: true });
      });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({ ...state, enough: state.loaded && state.count >= state.minToShow }), [state]);
  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
};

/** { loaded, reviews, count, minToShow, stats, enough } — `enough` = 3+ reviews exist. */
export const useReviews = () => useContext(ReviewsContext);
